import React from 'react';
import {
  Formik, Form as FormikForm, FormikHelpers, ErrorMessage,
} from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';

import media from '../../utils/mediaQueries';
import Field from '../fields/Field';
import Textareafield from '../fields/Textareafield';
import { SUCCESS_COLOR, ERROR_COLOR, FONT_FAMILY_MAIN } from '../../theme';
import Btn from '../buttons/Btn';
import { isMobile } from '../../constants';
import { FormTypes } from './types';

const Row = styled.div`
  margin: 16px 0 8px;
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
  letter-spacing: 0.3px;
  margin-bottom: 8px;

  ${(props) => props.error && `color: ${ERROR_COLOR};`}
`;

const SmallFieldWrap = styled.div`
  max-width: 272px;
`;

const BtnWrap = styled.div`
  margin-top: 40px;

  ${media.mobile`
    margin-top: 32px;
  `}
`;

const ResultMessage = styled.div`
  margin-top: 12px;
  color: ${(props) => props.isSuccess ? SUCCESS_COLOR : ERROR_COLOR};
  font-size: 14px;
  font-weight: bold;
  line-height: 1.29;
  letter-spacing: -0.1px;
`;

const ErrorMessageContainer = styled.div`
  font-family: ${FONT_FAMILY_MAIN};
  color: ${ERROR_COLOR};
  font-size: 12px;
  line-height: 1.17;
  margin-top: 3px;
`;

const validationSchema = Yup.object().shape({
  zipcode: Yup.string()
    .matches(/^[0-9]{5}$/, 'Ce champ doit comporter 5 chiffres.')
    .required('Ce champ est obligatoire.'),
  phone: Yup.string()
    .matches(
      /^(?:(?:)0)\s*[1-7,9](?:[\s.-]*\d{2}){4}$/,
      'Le téléphone doit être au format 01 02 03 04 05 et ne doit pas commencer par \'08\'',
    )
    .required('Ce champ est obligatoire.'),
});

type FormProps = {
  initialValues: FormTypes;
  handleSubmit(values: FormTypes, { setStatus, setSubmitting }: FormikHelpers<FormTypes>): void;
};

const Form = ({ initialValues, handleSubmit }: FormProps) => (
  <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
    {({
      values, errors, touched, handleChange, handleBlur, isSubmitting, status,
    }) => (
      <FormikForm>
        <Row>
          <Label error={errors.zipcode && touched.zipcode}>Votre code postal*</Label>
          <SmallFieldWrap>
            <Field
              placeholder="75018"
              type="text"
              name="zipcode"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.zipcode}
              valid={touched.zipcode && !errors.zipcode}
            />
          </SmallFieldWrap>
          <ErrorMessage component={ErrorMessageContainer} name="zipcode" />
        </Row>
        <Row>
          <Label error={errors.phone && touched.phone}>Numéro de téléphone portable*</Label>
          <SmallFieldWrap>
            <Field
              placeholder="06 05 04 03 02"
              type="text"
              name="phone"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.phone}
              valid={touched.phone && !errors.phone}
            />
          </SmallFieldWrap>
          <ErrorMessage component={ErrorMessageContainer} name="phone" />
        </Row>
        <Row>
          <Label>Vous avez un message pour nous ?</Label>
          <Textareafield
            name="message"
            value={values.message}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Row>
        <BtnWrap>
          <Btn
            type="submit"
            text="Envoyer"
            isLoading={isSubmitting}
            disabled={isSubmitting || (status && status.success)}
            block={isMobile}
          />
        </BtnWrap>
        {status && status.success && <ResultMessage isSuccess>Merci ! Vous allez être recontacté(e) sous peu</ResultMessage>}
        {status && !status.success && <ResultMessage>Une erreur est survenue, veuillez réessayer</ResultMessage>}
      </FormikForm>
    )}
  </Formik>
);

export { Form };
